import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { colors, spacing, shadows } from '../theme/index';
import { useAuth } from '../context/AuthContext';
import API_BASE_URL from '../config/api';

function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.message || 'Login failed');
        setLoading(false);
        return;
      }

      if (data.data.user.role !== 'admin') {
        setError('❌ Admin access only! This account is not an admin.');
        setLoading(false);
        return;
      }

      login(data.data.token, data.data.user);
      navigate('/secret-panel', { replace: true });
    } catch (err) {
      setError('Connection error. Make sure backend is running on port 5000');
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${colors.primary[600]} 0%, ${colors.secondary[600]} 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: spacing.lg,
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        style={{
          width: '100%',
          maxWidth: '450px',
        }}
      >
        {/* Lock Icon */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          style={{ textAlign: 'center', marginBottom: spacing['2xl'] }}
        >
          <motion.div
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
            style={{ fontSize: '4rem', marginBottom: spacing.lg }}
          >
            🔐
          </motion.div>
          <h1
            style={{
              fontSize: '2.5rem',
              fontWeight: 900,
              color: 'white',
              marginBottom: spacing.sm,
              letterSpacing: '-1px',
            }}
          >
            Admin Access
          </h1>
          <p
            style={{
              fontSize: '1rem',
              color: colors.primary[100],
              fontWeight: 500,
            }}
          >
            Secure Panel Login
          </p>
        </motion.div>

        {/* Login Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          style={{
            backgroundColor: 'white',
            borderRadius: '1rem',
            boxShadow: shadows['2xl'],
            padding: spacing['2xl'],
          }}
        >
          {error && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              style={{
                marginBottom: spacing.lg,
                padding: spacing.lg,
                backgroundColor: colors.error,
                color: 'white',
                borderRadius: '0.75rem',
                fontWeight: 600,
              }}
            >
              {error}
            </motion.div>
          )}

          <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg }}>
            {/* Email */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <label
                style={{
                  display: 'block',
                  color: colors.text.primary,
                  fontWeight: 700,
                  marginBottom: spacing.sm,
                  fontSize: '0.95rem',
                }}
              >
                Admin Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@surgical.com"
                style={{
                  width: '100%',
                  padding: spacing.md,
                  border: `2px solid ${colors.border}`,
                  borderRadius: '0.75rem',
                  fontSize: '1rem',
                  fontWeight: 500,
                  transition: 'all 0.3s ease',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = colors.primary[500];
                  e.target.style.boxShadow = `0 0 0 3px rgba(23, 139, 160, 0.1)`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = colors.border;
                  e.target.style.boxShadow = 'none';
                }}
                required
              />
            </motion.div>

            {/* Password */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <label
                style={{
                  display: 'block',
                  color: colors.text.primary,
                  fontWeight: 700,
                  marginBottom: spacing.sm,
                  fontSize: '0.95rem',
                }}
              >
                Admin Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                style={{
                  width: '100%',
                  padding: spacing.md,
                  border: `2px solid ${colors.border}`,
                  borderRadius: '0.75rem',
                  fontSize: '1rem',
                  fontWeight: 500,
                  transition: 'all 0.3s ease',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = colors.primary[500];
                  e.target.style.boxShadow = `0 0 0 3px rgba(23, 139, 160, 0.1)`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = colors.border;
                  e.target.style.boxShadow = 'none';
                }}
                required
              />
            </motion.div>

            {/* Login Button */}
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              type="submit"
              disabled={loading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              style={{
                width: '100%',
                background: `linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.primary[600]} 100%)`,
                color: 'white',
                padding: spacing.lg,
                borderRadius: '0.75rem',
                fontWeight: 800,
                fontSize: '1rem',
                border: 'none',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1,
                boxShadow: shadows.lg,
              }}
            >
              {loading ? '🔄 Logging in...' : '🔐 Login to Admin Panel'}
            </motion.button>
          </form>

          {/* Footer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            style={{
              marginTop: spacing.xl,
              paddingTop: spacing.lg,
              borderTop: `2px solid ${colors.border}`,
              textAlign: 'center',
              color: colors.text.secondary,
              fontSize: '0.85rem',
              fontWeight: 500,
            }}
          >
            For demo: admin@surgical.com / admin123
          </motion.div>
        </motion.div>
      </motion.div>
    </div>
  );
}

export default AdminLogin;
